import { Select, Input, SimpleGrid } from '@chakra-ui/react';

import SelectSearch from 'components/fields/SelectSearch';
import {
  SectionCard,
  ValidatedField,
  IntegerSelectWithOverflow,
} from '../../components';
import {
  useAnswerCodes,
  useValidationSchema,
  ValidationEntityProvider,
} from '../../hooks';
import type { Location } from 'api';

interface DwellingInformationCardProps {
  location: Location;
  locationIndex: number;
  onUpdate: (index: number, field: keyof Location, value: unknown) => void;
  isUpdating?: boolean;
  height?: string;
}

const DwellingInformationCard = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
  height,
}: DwellingInformationCardProps) => {
  const { getOptions, shouldShowConditionalField } = useAnswerCodes();
  const { isFieldRequiredByMarket } = useValidationSchema();

  const isFieldLocked = (field: string) =>
    location.itv_locked_fields?.includes(field) ?? false;

  const showExteriorWall = shouldShowConditionalField(
    'exterior_wall_type',
    location.location_type,
  );
  const showGarage = shouldShowConditionalField(
    'garage_type',
    location.location_type,
  );
  const showCars = shouldShowConditionalField(
    'number_of_cars',
    location.garage_type,
  );
  const showFullBaths = shouldShowConditionalField(
    'number_of_full_bathrooms',
    location.location_type,
  );
  const showHalfBaths = shouldShowConditionalField(
    'number_of_half_bathrooms',
    location.location_type,
  );
  const showKitchens = shouldShowConditionalField(
    'number_of_kitchens',
    location.location_type,
  );
  const showExactKitchens = shouldShowConditionalField(
    'kitchens',
    location.number_of_kitchens,
  );

  const showOtherConstruction = shouldShowConditionalField(
    'other_construction_type',
    location.construction_type,
  );

  const showOwnerFoundationType = shouldShowConditionalField(
    'building_foundation_type',
    location.location_type,
  );
  const showFoundationMaterialType =
    shouldShowConditionalField(
      'building_foundation_material_type',
      location.location_type,
    ) &&
    isFieldRequiredByMarket(
      'location',
      'building_foundation_material_type',
      location as unknown as Record<string, unknown>,
    );
  const showTenantCondoFoundationType = shouldShowConditionalField(
    'tenant_condo_building_foundation_type',
    location.location_type,
  );

  const showOtherExteriorWall = shouldShowConditionalField(
    'other_exterior_wall_type',
    location.exterior_wall_type,
  );

  const showOtherFoundationType = shouldShowConditionalField(
    'other_building_foundation_type',
    location.building_foundation_type,
  );

  const showOtherPropertyType = shouldShowConditionalField(
    'other_property_type',
    location.property_type,
  );
  const showOtherGarage = shouldShowConditionalField(
    'other_garage_type',
    location.garage_type,
  );

  const showTenantCondoExteriorWall = shouldShowConditionalField(
    'tenant_condo_exterior_wall_material_type',
    location.location_type,
  );

  const handleSelectChange =
    (field: keyof Location, otherField?: keyof Location) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onUpdate(locationIndex, field, e.target.value);
      if (
        otherField &&
        !shouldShowConditionalField(otherField as any, e.target.value)
      ) {
        onUpdate(locationIndex, otherField, '');
      }
    };

  const handleSelectSearchChange =
    (field: keyof Location, otherField?: keyof Location) =>
    (val: string | number) => {
      const strVal = String(val || '');
      onUpdate(locationIndex, field, strVal);
      if (
        otherField &&
        !shouldShowConditionalField(otherField as any, strVal)
      ) {
        onUpdate(locationIndex, otherField, '');
      }
    };

  const handleTextChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(locationIndex, field, e.target.value);
    };

  const handleNumberChange =
    (field: keyof Location) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
      onUpdate(locationIndex, field, isNaN(value as number) ? null : value);
    };

  return (
    <ValidationEntityProvider
      entity={location as unknown as Record<string, unknown>}
    >
      <SectionCard title="Dwelling Information" height={height}>
        <SimpleGrid columns={1} spacing={4}>
          <ValidatedField
            itemPath="location"
            fieldPath="construction_type"
            label="Primary Construction"
            isDisabled={isUpdating}
          >
            <Select
              variant="main"
              size="sm"
              value={location.construction_type || ''}
              onChange={handleSelectChange(
                'construction_type',
                'other_construction_type',
              )}
              isDisabled={isUpdating}
            >
              {getOptions('construction_type').map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </ValidatedField>

          {showOtherConstruction && (
            <ValidatedField
              itemPath="location"
              fieldPath="other_construction_type"
              label="Specify Construction Type"
              isDisabled={isUpdating}
            >
              <Input
                variant="main"
                size="sm"
                value={location.other_construction_type || ''}
                onChange={handleTextChange('other_construction_type')}
                isDisabled={isUpdating}
                placeholder="Describe construction type..."
              />
            </ValidatedField>
          )}

          {showOwnerFoundationType && (
            <ValidatedField
              itemPath="location"
              fieldPath="building_foundation_type"
              label="Foundation Type"
              isDisabled={
                isUpdating || isFieldLocked('building_foundation_type')
              }
            >
              <SelectSearch
                options={getOptions('building_foundation_type')}
                value={location.building_foundation_type || ''}
                onChange={(value) =>
                  onUpdate(
                    locationIndex,
                    'building_foundation_type',
                    value || '',
                  )
                }
                isDisabled={
                  isUpdating || isFieldLocked('building_foundation_type')
                }
                placeholder="Select foundation type..."
              />
            </ValidatedField>
          )}

          {showFoundationMaterialType && (
            <ValidatedField
              itemPath="location"
              fieldPath="building_foundation_material_type"
              label="Foundation Material"
              isDisabled={isUpdating}
            >
              <SelectSearch
                options={getOptions('building_foundation_material_type')}
                value={location.building_foundation_material_type || ''}
                onChange={(value) =>
                  onUpdate(
                    locationIndex,
                    'building_foundation_material_type',
                    value || '',
                  )
                }
                isDisabled={isUpdating}
                placeholder="Select foundation material..."
              />
            </ValidatedField>
          )}

          {showOtherFoundationType && (
            <ValidatedField
              itemPath="location"
              fieldPath="other_building_foundation_type"
              label="Specify Foundation Type"
              isDisabled={isUpdating}
            >
              <Input
                variant="main"
                size="sm"
                value={location.other_building_foundation_type || ''}
                onChange={handleTextChange('other_building_foundation_type')}
                isDisabled={isUpdating}
                placeholder="Describe foundation type..."
              />
            </ValidatedField>
          )}

          {showTenantCondoFoundationType && (
            <ValidatedField
              itemPath="location"
              fieldPath="tenant_condo_building_foundation_type"
              label="Building Foundation Type"
              isDisabled={isUpdating}
            >
              <SelectSearch
                options={getOptions('tenant_condo_building_foundation_type')}
                value={location.tenant_condo_building_foundation_type || ''}
                onChange={(value) =>
                  onUpdate(
                    locationIndex,
                    'tenant_condo_building_foundation_type',
                    value || '',
                  )
                }
                isDisabled={isUpdating}
                placeholder="Select foundation type..."
              />
            </ValidatedField>
          )}

          {showTenantCondoExteriorWall && (
            <ValidatedField
              itemPath="location"
              fieldPath="tenant_condo_exterior_wall_material_type"
              label="Exterior Wall Material"
              isDisabled={isUpdating}
            >
              <SelectSearch
                options={getOptions('tenant_condo_exterior_wall_material_type')}
                value={location.tenant_condo_exterior_wall_material_type || ''}
                onChange={(value) =>
                  onUpdate(
                    locationIndex,
                    'tenant_condo_exterior_wall_material_type',
                    value || '',
                  )
                }
                isDisabled={isUpdating}
                placeholder="Select exterior wall material..."
              />
            </ValidatedField>
          )}

          {showExteriorWall && (
            <ValidatedField
              itemPath="location"
              fieldPath="exterior_wall_type"
              label="Primary Exterior Finish"
              isDisabled={isUpdating || isFieldLocked('exterior_wall_type')}
            >
              <Select
                variant="main"
                size="sm"
                value={location.exterior_wall_type || ''}
                onChange={handleSelectChange(
                  'exterior_wall_type',
                  'other_exterior_wall_type',
                )}
                isDisabled={isUpdating || isFieldLocked('exterior_wall_type')}
              >
                {getOptions('exterior_wall_type').map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>
          )}

          {showOtherExteriorWall && (
            <ValidatedField
              itemPath="location"
              fieldPath="other_exterior_wall_type"
              label="Specify Exterior Finish"
              isDisabled={isUpdating}
            >
              <Input
                variant="main"
                size="sm"
                value={location.other_exterior_wall_type || ''}
                onChange={handleTextChange('other_exterior_wall_type')}
                isDisabled={isUpdating}
                placeholder="Describe exterior finish..."
              />
            </ValidatedField>
          )}

          <ValidatedField
            itemPath="location"
            fieldPath="property_type"
            label="Structure"
            isDisabled={isUpdating || isFieldLocked('property_type')}
          >
            <SelectSearch
              options={getOptions('property_type').filter(
                (opt) => opt.value !== '',
              )}
              value={location.property_type || ''}
              onChange={handleSelectSearchChange(
                'property_type',
                'other_property_type',
              )}
              placeholder="Select..."
              isDisabled={isUpdating || isFieldLocked('property_type')}
              allowClear
            />
          </ValidatedField>

          {showOtherPropertyType && (
            <ValidatedField
              itemPath="location"
              fieldPath="other_property_type"
              label="Specify Structure Type"
              isDisabled={isUpdating}
            >
              <Input
                variant="main"
                size="sm"
                value={location.other_property_type || ''}
                onChange={handleTextChange('other_property_type')}
                isDisabled={isUpdating}
                placeholder="Describe structure type..."
              />
            </ValidatedField>
          )}

          {showGarage && (
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
              <ValidatedField
                itemPath="location"
                fieldPath="garage_type"
                label="Garage"
                isDisabled={isUpdating || isFieldLocked('garage_type')}
              >
                <Select
                  variant="main"
                  size="sm"
                  value={location.garage_type || ''}
                  onChange={handleSelectChange(
                    'garage_type',
                    'other_garage_type',
                  )}
                  isDisabled={isUpdating || isFieldLocked('garage_type')}
                >
                  {getOptions('garage_type').map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </ValidatedField>

              {showCars && (
                <ValidatedField
                  itemPath="location"
                  fieldPath="number_of_cars"
                  label="# of Cars"
                  isDisabled={isUpdating || isFieldLocked('number_of_cars')}
                >
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    min={0}
                    value={location.number_of_cars ?? ''}
                    onChange={handleNumberChange('number_of_cars')}
                    isDisabled={isUpdating || isFieldLocked('number_of_cars')}
                  />
                </ValidatedField>
              )}
            </SimpleGrid>
          )}

          {showOtherGarage && (
            <ValidatedField
              itemPath="location"
              fieldPath="other_garage_type"
              label="Specify Garage Type"
              isDisabled={isUpdating}
            >
              <Input
                variant="main"
                size="sm"
                value={location.other_garage_type || ''}
                onChange={handleTextChange('other_garage_type')}
                isDisabled={isUpdating}
                placeholder="Describe garage type..."
              />
            </ValidatedField>
          )}

          <SimpleGrid columns={{ base: 1 }} spacing={4}>
            <ValidatedField
              itemPath="location"
              fieldPath="number_of_storeys"
              label="Storeys"
              isDisabled={isUpdating || isFieldLocked('number_of_storeys')}
              formControlProps={{ whiteSpace: 'nowrap' }}
            >
              <Select
                variant="main"
                size="sm"
                value={location.number_of_storeys || ''}
                onChange={handleSelectChange('number_of_storeys')}
                isDisabled={isUpdating || isFieldLocked('number_of_storeys')}
              >
                {getOptions('number_of_storeys').map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>

            {shouldShowConditionalField(
              'storeys',
              location.number_of_storeys,
            ) && (
              <ValidatedField
                itemPath="location"
                fieldPath="storeys"
                label="Exact Number of Storeys"
                isDisabled={isUpdating || isFieldLocked('storeys')}
                formControlProps={{ whiteSpace: 'nowrap' }}
              >
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  min={5}
                  value={location.storeys ?? ''}
                  onChange={handleNumberChange('storeys')}
                  isDisabled={isUpdating || isFieldLocked('storeys')}
                />
              </ValidatedField>
            )}

            {shouldShowConditionalField(
              'building_shape',
              location.location_type,
            ) && (
              <ValidatedField
                itemPath="location"
                fieldPath="building_shape"
                label="Building Shape"
                isDisabled={isUpdating}
                formControlProps={{ whiteSpace: 'nowrap' }}
              >
                <Select
                  variant="main"
                  size="sm"
                  value={location.building_shape || ''}
                  onChange={handleSelectChange('building_shape')}
                  isDisabled={isUpdating}
                >
                  {getOptions('building_shape').map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </ValidatedField>
            )}

            <IntegerSelectWithOverflow
              itemPath="location"
              fieldPath="number_of_selfcontained_units_in_property"
              label="Units"
              exactLabel="Exact # of Units"
              value={location.number_of_selfcontained_units_in_property}
              onChange={(val) =>
                onUpdate(
                  locationIndex,
                  'number_of_selfcontained_units_in_property',
                  val,
                )
              }
              isDisabled={isUpdating}
            />

            <IntegerSelectWithOverflow
              itemPath="location"
              fieldPath="number_of_families"
              label="Families"
              exactLabel="Exact # of Families"
              value={location.number_of_families}
              onChange={(val) =>
                onUpdate(locationIndex, 'number_of_families', val)
              }
              isDisabled={isUpdating}
            />

            {showKitchens && (
              <ValidatedField
                itemPath="location"
                fieldPath="number_of_kitchens"
                label="Kitchens"
                isDisabled={isUpdating || isFieldLocked('number_of_kitchens')}
                formControlProps={{ whiteSpace: 'nowrap' }}
              >
                <Select
                  variant="main"
                  size="sm"
                  value={location.number_of_kitchens || ''}
                  onChange={handleSelectChange('number_of_kitchens')}
                  isDisabled={isUpdating || isFieldLocked('number_of_kitchens')}
                >
                  {getOptions('number_of_kitchens').map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </ValidatedField>
            )}

            {showExactKitchens && (
              <ValidatedField
                itemPath="location"
                fieldPath="kitchens"
                label="Exact # of Kitchens"
                isDisabled={isUpdating || isFieldLocked('kitchens')}
                formControlProps={{ whiteSpace: 'nowrap' }}
              >
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  min={4}
                  value={location.kitchens ?? ''}
                  onChange={handleNumberChange('kitchens')}
                  isDisabled={isUpdating || isFieldLocked('kitchens')}
                />
              </ValidatedField>
            )}

            {showFullBaths && (
              <IntegerSelectWithOverflow
                itemPath="location"
                fieldPath="number_of_full_bathrooms"
                label="Full Baths"
                exactLabel="Exact # of Full Baths"
                value={location.number_of_full_bathrooms}
                onChange={(val) =>
                  onUpdate(locationIndex, 'number_of_full_bathrooms', val)
                }
                isDisabled={isUpdating}
                isLocked={isFieldLocked('number_of_full_bathrooms')}
              />
            )}

            {showHalfBaths && (
              <IntegerSelectWithOverflow
                itemPath="location"
                fieldPath="number_of_half_bathrooms"
                label="Half Baths"
                exactLabel="Exact # of Half Baths"
                value={location.number_of_half_bathrooms}
                onChange={(val) =>
                  onUpdate(locationIndex, 'number_of_half_bathrooms', val)
                }
                isDisabled={isUpdating}
              />
            )}
          </SimpleGrid>
        </SimpleGrid>
      </SectionCard>
    </ValidationEntityProvider>
  );
};

export default DwellingInformationCard;
